import { useMemo, useState } from 'react'

import { useQueryClient } from '@tanstack/react-query'

import { useDecodedAccessToken } from '@b-stock/fe-core/bstock-next/components'
import { useFlag } from '@b-stock/fe-core/bstock-next/helpers'

import { getWatchlistDateExpires } from '@/helpers/dateUtils'
import { FeatureFlags } from '@/helpers/featureFlags'
import { invalidateListingNotes } from '@/queries/listingNotesQuery'
import {
  allWatchedListingsQuery,
  usePutWatchedListing,
} from '@/queries/watchlistQuery'

export type BulkAddToWatchlistResult = {
  addedCount: number
  skippedCount: number
  failedCount: number
  attempted: number
  // listingIds that were actually added this call — lets callers fire a
  // per-listing analytics event without the hook itself depending on
  // analytics (surface/screenName differ per caller).
  addedListingIds: string[]
}

// Minimal shape needed to bulk-add a listing to the watchlist — deliberately
// narrower than any one surface's search-result type so both the
// CombinedListingSearchResult-based ALP/Storefront flow and the
// BidsSearchResult-based My Bids flow can supply it directly.
export type BulkWatchlistableListing = {
  listingId: string
  endTime: Date
}

export function useBulkAddToWatchlist(listings: BulkWatchlistableListing[]) {
  const decodedAccessToken = useDecodedAccessToken()
  const { personId, accountId } = decodedAccessToken ?? {}
  const isClosingAlertEnabled = useFlag(FeatureFlags.WATCHLIST_CLOSING_ALERT)
  const queryClient = useQueryClient()
  const putWatchlistListingMutation = usePutWatchedListing({
    refreshesListingNotes: false,
  })

  const { data: watchlistEntries } = allWatchedListingsQuery.useWithOpts(
    [personId ?? ''],
    { enabled: !!personId }
  )
  const watchedIds = useMemo(
    () => new Set((watchlistEntries ?? []).map((entry) => entry.listingId)),
    [watchlistEntries]
  )

  const toAdd = useMemo(
    () => listings.filter((listing) => !watchedIds.has(listing.listingId)),
    [listings, watchedIds]
  )

  const [isSubmitting, setIsSubmitting] = useState(false)

  const submit = async (): Promise<BulkAddToWatchlistResult> => {
    const skippedCount = listings.length - toAdd.length

    if (!personId || !accountId || toAdd.length === 0) {
      return {
        addedCount: 0,
        skippedCount,
        failedCount: 0,
        attempted: 0,
        addedListingIds: [],
      }
    }

    setIsSubmitting(true)
    try {
      const results = await Promise.allSettled(
        // The callback is `async` so a synchronous throw while building the
        // payload (e.g. getWatchlistDateExpires on a malformed endTime) rejects
        // only that listing's settlement instead of throwing out of `.map()`
        // and aborting the whole batch before Promise.allSettled ever runs.
        toAdd.map(async (listing) => {
          await putWatchlistListingMutation.mutateAsync({
            personId,
            listingId: listing.listingId,
            createWatchedListingDto: {
              accountId,
              dateExpires: getWatchlistDateExpires(listing.endTime),
              ...(isClosingAlertEnabled
                ? { listingEndTime: listing.endTime.toISOString() }
                : {}),
            },
          })
          return listing.listingId
        })
      )

      const addedListingIds = results
        .filter(
          (result): result is PromiseFulfilledResult<string> =>
            result.status === 'fulfilled'
        )
        .map((result) => result.value)
      const failedCount = toAdd.length - addedListingIds.length

      void queryClient.invalidateQueries({
        queryKey: [allWatchedListingsQuery.name],
      })
      if (addedListingIds.length > 0) void invalidateListingNotes(queryClient)

      return {
        addedCount: addedListingIds.length,
        skippedCount,
        failedCount,
        attempted: toAdd.length,
        addedListingIds,
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  return { submit, isPending: isSubmitting }
}
